/**
 * Filter Constants
 * 
 * Centralized configuration for filter options across all management modules.
 * This ensures consistency and makes it easy to modify filter options.
 * 
 * @module FilterConstants
 */

/**
 * Common status filter options used across all modules
 */
export const STATUS_FILTER_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'All', value: 'all' }
];

/**
 * User role filter options
 */
export const ROLE_FILTER_OPTIONS = [
  { label: 'Admin', value: 'admin' },
  { label: 'Supervisor', value: 'supervisor' },
  { label: 'User', value: 'user' }
];

/**
 * Provider specialty filter options
 */
export const SPECIALTY_FILTER_OPTIONS = [
  { label: 'Cardiology', value: 'cardiology' },
  { label: 'Dermatology', value: 'dermatology' },
  { label: 'Internal Medicine', value: 'internal-medicine' },
  { label: 'Neurology', value: 'neurology' },
  { label: 'Oncology', value: 'oncology' },
  { label: 'Pediatrics', value: 'pediatrics' },
  { label: 'Psychiatry', value: 'psychiatry' },
  { label: 'Surgery', value: 'surgery' }
];

/**
 * Facility filter group configurations
 */
export const FACILITY_FILTER_GROUPS = [
  {
    name: 'Status',
    key: 'status',
    options: STATUS_FILTER_OPTIONS
  }
  // Future filter groups can be added here:
  // {
  //   name: 'Location',
  //   key: 'location',
  //   options: LOCATION_FILTER_OPTIONS
  // },
  // {
  //   name: 'Insurance Plans',
  //   key: 'plans',
  //   options: PLANS_FILTER_OPTIONS
  // }
];

/**
 * User management filter group configurations
 */
export const USER_FILTER_GROUPS = [
  {
    name: 'Status',
    key: 'status',
    options: STATUS_FILTER_OPTIONS
  }
  // Future filter groups:
  // {
  //   name: 'Role',
  //   key: 'role',
  //   options: ROLE_FILTER_OPTIONS
  // },
  // {
  //   name: 'Facility',
  //   key: 'facility',
  //   options: [] // Will be populated dynamically with facility data
  // }
];

/**
 * Provider filter group configurations
 */
export const PROVIDER_FILTER_GROUPS = [
  {
    name: 'Status',
    key: 'status',
    options: STATUS_FILTER_OPTIONS
  }
  // Future filter groups:
  // {
  //   name: 'Specialty',
  //   key: 'specialty',
  //   options: SPECIALTY_FILTER_OPTIONS
  // },
  // {
  //   name: 'Facility',
  //   key: 'facility',
  //   options: [] // Will be populated dynamically with facility data
  // }
];

/**
 * Department filter group configurations
 * Note: Facility options will be populated dynamically with facility data
 */
export const DEPARTMENT_FILTER_GROUPS = [
  {
    name: 'Status',
    key: 'status',
    options: STATUS_FILTER_OPTIONS
  },
  {
    name: 'Facility',
    key: 'facility',
    options: [] // Will be populated dynamically with facility data
  }
];

/**
 * Batch status filter options
 */
export const BATCH_STATUS_FILTER_OPTIONS = [
  { label: 'All Statuses', value: 'all' },
  { label: 'Scheduled', value: 'scheduled' },
  { label: 'Completed', value: 'completed' },
  { label: 'Cancelled', value: 'cancelled' }
];

/**
 * Batch filter group configurations
 * Note: Dynamic options will be populated with API data
 */
export const BATCH_FILTER_GROUPS = [
  {
    name: 'Status',
    key: 'status',
    options: BATCH_STATUS_FILTER_OPTIONS
  },
  {
    name: 'Branch',
    key: 'branch',
    options: [] // Will be populated dynamically from customer details
  },
  {
    name: 'Usecase',
    key: 'usecase',
    options: [] // Will be populated dynamically from customer details
  },
  {
    name: 'Sub Usecase',
    key: 'subUsecase',
    options: [] // Will be populated dynamically from customer details
  },
  {
    name: 'Uploader',
    key: 'uploaderName',
    options: [] // Will be populated dynamically from staff data
  }
];

/**
 * Default filter values for each module
 */
export const DEFAULT_FILTERS = {
  facility: { status: 'active' },
  user: { status: 'active' },
  provider: { status: 'active' },
  department: { status: 'active', facility: 'all' },
  batch: { 
    status: 'all', 
    branch: 'all', 
    usecase: 'all', 
    subUsecase: 'all', 
    uploaderName: 'all',
    search: '' 
  }
};

/**
 * Search placeholder texts for different modules
 */
export const SEARCH_PLACEHOLDERS = {
  user: "Search by name, email, or role...",
  facility: "Search by name, phone, NPI, Address, or Tax ID...",
  department: "Search by name or email...",
  callHistory: "Search by member name or ID...",
};

export const CALL_HISTORY_FILTER_GROUPS = [
  {
    label: "Status",
    key: "call_status",
    options: [
      { label: "All", value: "" },
      { label: "Scheduled", value: "scheduled" },
      { label: "Cancelled", value: "cancelled" },
      { label: "Successful", value: "successful" },
      { label: "Failure", value: "failure" },
    ],
  },
];

/**
 * Status toggle action types
 */
export const TOGGLE_ACTIONS = {
  ACTIVATE: 'activate',
  DEACTIVATE: 'deactivate'
};

/**
 * Status toggle messages
 */
export const TOGGLE_MESSAGES = {
  facility: {
    deactivate: 'This will make the facility inactive and may affect related operations.',
    activate: 'This will restore the facility to active status.'
  },
  user: {
    deactivate: 'This will make the user inactive and may affect their access.',
    activate: 'This will restore the user to active status.'
  },
  provider: {
    deactivate: 'This will make the provider inactive and may affect their availability.',
    activate: 'This will restore the provider to active status.'
  },
  department: {
    deactivate: 'This will make the department inactive and may affect related operations.',
    activate: 'This will restore the department to active status.'
  }
};

/**
 * Permission-based action configurations
 */
export const PERMISSIONS = {
  ADMIN_ONLY: ['admin'],
  ADMIN_SUPERVISOR: ['admin', 'supervisor'],
  ALL_ROLES: ['admin', 'supervisor', 'user']
};

/**
 * Module-specific permissions
 */
export const MODULE_PERMISSIONS = {
  facility: {
    view: PERMISSIONS.ALL_ROLES,
    create: PERMISSIONS.ADMIN_SUPERVISOR,
    edit: PERMISSIONS.ADMIN_ONLY,
    toggleStatus: PERMISSIONS.ADMIN_ONLY
  },
  user: {
    view: PERMISSIONS.ALL_ROLES,
    create: PERMISSIONS.ADMIN_SUPERVISOR,
    edit: PERMISSIONS.ADMIN_ONLY,
    toggleStatus: PERMISSIONS.ADMIN_ONLY
  },
  provider: {
    view: PERMISSIONS.ALL_ROLES,
    create: PERMISSIONS.ADMIN_SUPERVISOR,
    edit: PERMISSIONS.ADMIN_ONLY,
    toggleStatus: PERMISSIONS.ADMIN_ONLY
  },
  department: {
    view: PERMISSIONS.ALL_ROLES,
    create: PERMISSIONS.ADMIN_SUPERVISOR,
    edit: PERMISSIONS.ADMIN_SUPERVISOR,
    delete: PERMISSIONS.ADMIN_ONLY,
    toggleStatus: PERMISSIONS.ADMIN_SUPERVISOR
  },
  batch: {
    view: PERMISSIONS.ALL_ROLES,
    create: PERMISSIONS.ALL_ROLES,
    edit: PERMISSIONS.ALL_ROLES,
    delete: PERMISSIONS.ALL_ROLES,
    toggleStatus: PERMISSIONS.ALL_ROLES
  }
};
